import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ordersApi } from '@/lib/api/orders';
import { inventoryApi } from '@/lib/api/inventory';
import { locationsApi } from '@/lib/api/locations';
import { getManifests } from '@/features/manifests/api/manifests-api';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import type { InventoryItem, Location, InventoryStatus } from '@/types/inventory';
import type { Manifest } from '@/types/manifest';

interface SortableRowProps {
  item: InventoryItem;
  index: number;
  isSelected: boolean;
  onToggle: (id: number) => void;
}

function SortableRow({ item, index, isSelected, onToggle }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-900 text-green-200';
      case 'Assigned':
        return 'bg-yellow-900 text-yellow-200';
      case 'Picked':
        return 'bg-blue-900 text-blue-200';
      case 'Removed':
        return 'bg-red-900 text-red-200';
      default:
        return 'bg-gray-700 text-gray-400';
    }
  };

  return (
    <tr ref={setNodeRef} style={style} className={`border-b border-gray-700 ${!isSelected ? 'opacity-40' : ''}`}>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(item.id)}
          className="w-4 h-4 accent-blue-500 cursor-pointer"
        />
      </td>
      <td
        {...attributes}
        {...listeners}
        className="px-2 py-2 text-center text-gray-500 cursor-move"
      >
        <span className="text-lg">⋮⋮</span>
      </td>
      <td className="px-4 py-2 text-gray-400">{index + 1}</td>
      <td className="px-4 py-2 text-gray-400">{item.id}</td>
      <td className="px-4 py-2 font-mono text-blue-400">{item.barcodeValue}</td>
      <td className="px-4 py-2">
        <span className={`px-2 py-1 text-xs rounded ${getStatusColor(item.status)}`}>
          {item.status}
        </span>
      </td>
      <td className="px-4 py-2">{item.locationCode || '-'}</td>
      <td className="px-4 py-2">{item.sortId1 || '-'}</td>
      <td className="px-4 py-2">{item.sortId2 || '-'}</td>
      <td className="px-4 py-2 text-xs text-gray-400">
        {item.locationAssignedAt ? new Date(item.locationAssignedAt).toLocaleString() : '-'}
      </td>
    </tr>
  );
}

export function CreateOrderPage() {
  const { dialog, closeDialog, showSuccess, showError, showWarning } = useDialog();
  const [locations, setLocations] = useState<Location[]>([]);
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [previewItems, setPreviewItems] = useState<InventoryItem[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFirstLoad = useRef(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const [isOpenOrder, setIsOpenOrder] = useState(false);
  const [createOnHold, setCreateOnHold] = useState(false);
  const [manifestSearch, setManifestSearch] = useState('');
  const [manifestDropdownOpen, setManifestDropdownOpen] = useState(false);
  const manifestDropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    orderNumber: '',
    truckNumber: '',
    truckName: '',
    customerName: '',
    productName: '',
    expectedItemCount: '',
    comments: '',
    sortId1Filter: '',
    sortId2Filter: '',
    locationIdFilter: '',
    manifestIdFilter: '',
    maxItems: '',
    useLifo: true,
  });

  const loadLocations = async () => {
    try {
      const data = await locationsApi.getAll();
      setLocations(data ?? []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadManifests = async () => {
    try {
      const response = await getManifests();
      setManifests(response.data ?? []);
    } catch (error) {
      console.error('Error loading manifests:', error);
    }
  };

  const loadPreview = useCallback(async () => {
    try {
      setIsLoadingPreview(true);
      const params: {
        pageSize: number;
        status: InventoryStatus;
        locationId?: number;
        manifestId?: number;
        sortId1?: string;
        sortId2?: string;
      } = {
        pageSize: formData.maxItems ? parseInt(formData.maxItems) : 500,
        status: 'Active' as InventoryStatus,
      };

      if (formData.locationIdFilter) params.locationId = parseInt(formData.locationIdFilter);
      if (formData.manifestIdFilter) params.manifestId = parseInt(formData.manifestIdFilter);
      if (formData.sortId1Filter) params.sortId1 = formData.sortId1Filter;
      if (formData.sortId2Filter) params.sortId2 = formData.sortId2Filter;

      const data = await inventoryApi.getInventory(params);
      // Filter to only show Active items (exclude Picked, Assigned, Removed)
      const activeItems = (data?.items ?? []).filter(item => item.status === 'Active');

      setPreviewItems(prev => {
        // Keep previously-selected items that aren't in the new results (multi-location support).
        const newIds = new Set(activeItems.map(i => i.id));
        const keptFromPrev = prev.filter(i => selectedItemIds.has(i.id) && !newIds.has(i.id));
        return [...keptFromPrev, ...activeItems];
      });

      isFirstLoad.current = false;
      // Never auto-select — user picks items manually or uses "Select All Visible"
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [formData.locationIdFilter, formData.manifestIdFilter, formData.maxItems, formData.sortId1Filter, formData.sortId2Filter, formData.useLifo]);

  useEffect(() => {
    loadLocations();
    loadManifests();
  }, []);

  useEffect(() => {
    // Debounce text inputs (maxItems, sortId filters) to avoid rapid API calls on keystrokes
    const timer = setTimeout(() => {
      loadPreview();
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.sortId1Filter, formData.sortId2Filter, formData.locationIdFilter, formData.useLifo, loadPreview]);

  // Close manifest dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (manifestDropdownRef.current && !manifestDropdownRef.current.contains(e.target as Node)) {
        setManifestDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredManifests = manifests.filter(m =>
    m.bargeName.toLowerCase().includes(manifestSearch.toLowerCase())
  );

  const selectedManifest = manifests.find(m => String(m.id) === formData.manifestIdFilter);

  const handleToggleItem = (id: number) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedItemIds.size === previewItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(previewItems.map(item => item.id)));
    }
  };

  // Get current visible items (items from latest filter results, not previously-selected holdovers)
  const visibleItemIds = new Set(previewItems.map(item => item.id));

  const handleSelectAllVisible = () => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      visibleItemIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleDeselectAllVisible = () => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      visibleItemIds.forEach(id => next.delete(id));
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPreviewItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const checkedItems = previewItems.filter(item => selectedItemIds.has(item.id));

    // A blank order staged "on hold" with no items must be created as an open order so the
    // server accepts it without a pre-selected item list.
    const stageAsOpenOrder = createOnHold && !isOpenOrder && checkedItems.length === 0;

    if (!isOpenOrder && !stageAsOpenOrder && checkedItems.length === 0) {
      showError('No Items Selected', 'Please select at least one item for the order.');
      return;
    }

    // Validate the optional expected item count (open orders only). HTML min/max are not
    // enforced on typed input, so guard explicitly: whole number in [1, 10000] or blank.
    let expectedItemCount: number | undefined;
    if (isOpenOrder && formData.expectedItemCount.trim()) {
      const parsed = parseInt(formData.expectedItemCount, 10);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 10000) {
        showError('Invalid Expected Count', 'Expected item count must be a whole number between 1 and 10000.');
        return;
      }
      expectedItemCount = parsed;
    }

    try {
      setIsSubmitting(true);

      let createdOrder;
      if (isOpenOrder || stageAsOpenOrder) {
        createdOrder = await ordersApi.create({
          orderNumber: formData.orderNumber || undefined,
          truckNumber: formData.truckNumber || undefined,
          truckName: formData.truckName || undefined,
          customerName: formData.customerName || undefined,
          comments: formData.comments || undefined,
          isOpenOrder: true,
          productName: formData.productName || undefined,
          expectedItemCount,
        });
      } else {
        // Get ordered item IDs (only checked items, in drag order)
        const orderedItemIds = checkedItems.map(item => item.id);

        createdOrder = await ordersApi.create({
          orderNumber: formData.orderNumber || undefined,
          truckNumber: formData.truckNumber || undefined,
          truckName: formData.truckName || undefined,
          customerName: formData.customerName || undefined,
          comments: formData.comments || undefined,
          sortId1Filter: formData.sortId1Filter || undefined,
          sortId2Filter: formData.sortId2Filter || undefined,
          locationIdFilter: formData.locationIdFilter ? parseInt(formData.locationIdFilter) : undefined,
          manifestIdFilter: formData.manifestIdFilter ? parseInt(formData.manifestIdFilter) : undefined,
          maxItems: formData.maxItems ? parseInt(formData.maxItems) : undefined,
          useLifo: formData.useLifo,
          customItemOrder: orderedItemIds,
        });
      }

      if (createOnHold && createdOrder) {
        // The order already exists at this point. A failed hold must NOT surface a
        // generic "create failed" error — that would invite the operator to submit
        // again and create a duplicate. Send them to the created order to hold it
        // manually instead.
        try {
          await ordersApi.hold(createdOrder.id);
        } catch (holdError) {
          console.error('Order created but hold failed:', holdError);
          const createdId = createdOrder.id;
          showWarning(
            'Order Created — Not On Hold',
            'The order was created but could not be placed on hold. Opening it so you can put it on hold manually.',
            () => { window.location.href = `/orders/${createdId}`; }
          );
          return;
        }
      }

      showSuccess('Success', createOnHold ? 'Order created on hold!' : 'Order created successfully!', () => {
        window.location.href = '/orders';
      });
    } catch (error) {
      // Reaching here means create() itself failed — nothing was persisted, so it is
      // safe to re-enable the form for a retry.
      console.error('Error creating order:', error);
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to create order';
      showError('Error', message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <a href="/orders" className="text-blue-400 hover:text-blue-300">
          ← Back to Orders
        </a>
        <h1 className="text-3xl font-bold">Create Order</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Order Details */}
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Order Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Release Number (optional)</label>
              <input
                type="text"
                value={formData.orderNumber}
                onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                maxLength={50}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Truck Number (optional)</label>
              <input
                type="text"
                value={formData.truckNumber}
                onChange={(e) => setFormData({ ...formData, truckNumber: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                maxLength={50}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Truck Name (optional)</label>
              <input
                type="text"
                value={formData.truckName}
                onChange={(e) => setFormData({ ...formData, truckName: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                maxLength={200}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Customer Name (optional)</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                maxLength={200}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
              placeholder="Optional notes or description for this order"
              rows={2}
              maxLength={2000}
            />
          </div>
        </div>

        {/* Open Order toggle */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="isOpenOrder"
              checked={isOpenOrder}
              onChange={(e) => setIsOpenOrder(e.target.checked)}
              className="mt-1 w-5 h-5 accent-blue-500 cursor-pointer"
            />
            <div>
              <label htmlFor="isOpenOrder" className="text-lg font-semibold cursor-pointer">
                Open Order
              </label>
              <p className="text-sm text-gray-400 mt-1">
                Items are picked dynamically by scanning — no pre-defined item list. Items scanned
                are immediately marked as picked and will not appear in Needs a Home.
              </p>
            </div>
          </div>

          {isOpenOrder && (
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Product Name</label>
              <input
                type="text"
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Optional — helps identify what this open order is for"
                maxLength={200}
              />

              <label htmlFor="expectedItemCount" className="block text-sm font-medium mb-1 mt-4">
                Expected item count
              </label>
              <input
                id="expectedItemCount"
                data-testid="expected-item-count-input"
                type="number"
                min={1}
                max={10000}
                value={formData.expectedItemCount}
                onChange={(e) =>
                  setFormData({ ...formData, expectedItemCount: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Optional — order can't be completed until this many items are put on"
              />
            </div>
          )}
        </div>

        {/* Inventory Filters + Preview — hidden for open orders */}
        {!isOpenOrder && (
        <>
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Inventory Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sort ID 1</label>
              <input
                type="text"
                value={formData.sortId1Filter}
                onChange={(e) => setFormData({ ...formData, sortId1Filter: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Filter by Sort ID 1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Sort ID 2</label>
              <input
                type="text"
                value={formData.sortId2Filter}
                onChange={(e) => setFormData({ ...formData, sortId2Filter: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Filter by Sort ID 2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <select
                value={formData.locationIdFilter}
                onChange={(e) => setFormData({ ...formData, locationIdFilter: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Max Items</label>
              <input
                type="number"
                value={formData.maxItems}
                onChange={(e) => setFormData({ ...formData, maxItems: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
                placeholder="Limit (optional)"
                min="1"
                max="10000"
              />
            </div>

            <div ref={manifestDropdownRef} className="relative">
              <label className="block text-sm font-medium mb-1">Manifest</label>
              <div
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus-within:border-blue-500 flex items-center cursor-text"
                onClick={() => setManifestDropdownOpen(true)}
              >
                {manifestDropdownOpen ? (
                  <input
                    type="text"
                    value={manifestSearch}
                    onChange={(e) => setManifestSearch(e.target.value)}
                    className="w-full bg-transparent outline-none"
                    placeholder="Search manifests..."
                    autoFocus
                  />
                ) : (
                  <span className={selectedManifest ? 'text-white' : 'text-gray-400'}>
                    {selectedManifest ? selectedManifest.bargeName : 'All Manifests'}
                  </span>
                )}
                {formData.manifestIdFilter && !manifestDropdownOpen && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, manifestIdFilter: '' });
                      setManifestSearch('');
                    }}
                    className="ml-auto text-gray-400 hover:text-white text-sm"
                  >
                    ✕
                  </button>
                )}
              </div>
              {manifestDropdownOpen && (
                <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-gray-700 border border-gray-600 rounded shadow-lg">
                  <li
                    className={`px-3 py-2 cursor-pointer hover:bg-gray-600 ${!formData.manifestIdFilter ? 'text-blue-400' : 'text-gray-300'}`}
                    onClick={() => {
                      setFormData({ ...formData, manifestIdFilter: '' });
                      setManifestSearch('');
                      setManifestDropdownOpen(false);
                    }}
                  >
                    All Manifests
                  </li>
                  {filteredManifests.map((m) => (
                    <li
                      key={m.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-600 ${String(m.id) === formData.manifestIdFilter ? 'text-blue-400' : 'text-gray-300'}`}
                      onClick={() => {
                        setFormData({ ...formData, manifestIdFilter: String(m.id) });
                        setManifestSearch('');
                        setManifestDropdownOpen(false);
                      }}
                    >
                      {m.bargeName}
                    </li>
                  ))}
                  {filteredManifests.length === 0 && (
                    <li className="px-3 py-2 text-gray-500 italic">No manifests found</li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="useLifo"
              checked={formData.useLifo}
              onChange={(e) => setFormData({ ...formData, useLifo: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="useLifo" className="text-sm">
              Use LIFO (Last In, First Out) ordering
            </label>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              Preview ({selectedItemIds.size} selected / {previewItems.length} visible)
            </h2>
            <div className="flex items-center gap-2">
              {isLoadingPreview && <span className="text-sm text-gray-400">Loading...</span>}
              {previewItems.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleSelectAllVisible}
                    className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                  >
                    Select All Visible
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAllVisible}
                    className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-500 rounded transition-colors"
                  >
                    Deselect All Visible
                  </button>
                </>
              )}
            </div>
          </div>

          {previewItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              {isLoadingPreview ? 'Loading inventory...' : 'No active inventory items match the current filters'}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <div className="max-h-96 overflow-y-auto overflow-x-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-center w-10">
                        <input
                          type="checkbox"
                          checked={selectedItemIds.size === previewItems.length && previewItems.length > 0}
                          onChange={handleSelectAll}
                          className="w-4 h-4 accent-blue-500 cursor-pointer"
                          title="Select all"
                        />
                      </th>
                      <th className="px-2 py-2 text-center w-12"></th>
                      <th className="px-4 py-2 text-left">Order</th>
                      <th className="px-4 py-2 text-left">ID</th>
                      <th className="px-4 py-2 text-left">Barcode</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Location</th>
                      <th className="px-4 py-2 text-left">Sort ID 1</th>
                      <th className="px-4 py-2 text-left">Sort ID 2</th>
                      <th className="px-4 py-2 text-left">Assigned At</th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext
                      items={previewItems.map(item => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {previewItems.map((item, index) => (
                        <SortableRow
                          key={item.id}
                          item={item}
                          index={index}
                          isSelected={selectedItemIds.has(item.id)}
                          onToggle={handleToggleItem}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </div>
            </DndContext>
          )}
        </div>
        </> )} {/* end !isOpenOrder */}

        {/* Submit */}
        <div className="flex items-center gap-4 justify-end">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={createOnHold}
              onChange={(e) => setCreateOnHold(e.target.checked)}
              className="w-4 h-4 accent-orange-500 cursor-pointer"
            />
            Create on hold
          </label>
          <a
            href="/orders"
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
          >
            Cancel
          </a>
          <button
            type="submit"
            disabled={isSubmitting || (!isOpenOrder && !createOnHold && selectedItemIds.size === 0)}
            className={`px-6 py-3 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${createOnHold ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isSubmitting ? 'Creating...' : createOnHold ? 'Create on Hold' : 'Create Order'}
          </button>
        </div>
      </form>

      {/* Dialog */}
      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
